import { Injectable, inject, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { CustomModalController } from '../services/custom-modal-controller.service';
import { LoginModalComponent } from '../../components/login-modal/login-modal.component';
import { SwNavigatorService } from '../services/sw-navigator.service';
import { ScreenLayer } from '../enums/screen-layer.enum';
import { StorageService, STORAGE_KEYS } from '../services/storage.service';

/**
 * Global HTTP Interceptor that adds Bearer token and withCredentials to all API requests.
 * 
 * Error handling strategy:
 * - 401 Unauthorized: Show login modal (no refresh token logic)
 * - 403 Forbidden: Pass through
 * 
 * Session is managed by httpOnly cookie — no refresh tokens.
 * 
 * NOTE: AuthService is resolved lazily via Injector to avoid a circular dependency:
 * HTTP_INTERCEPTORS → HttpConfigInterceptor → AuthService → HttpClient → HTTP_INTERCEPTORS
 */
@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);
  private modalCtrl = inject(CustomModalController);
  private swNavigator = inject(SwNavigatorService);
  private storageService = inject(StorageService);

  private _authService: AuthService | null = null;

  /** Lazily resolve AuthService to break the circular dependency */
  private get authService(): AuthService {
    if (!this._authService) {
      this._authService = this.injector.get(AuthService);
    }
    return this._authService;
  }

  private loginModalOpen = false;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiRequest = req.url.includes('/api/');
    const isAuthRequest = req.url.includes('/auth/');
    // /users/me is used during init (checkAuthOnInit) — must NOT wait for authReady$
    // to avoid deadlock (authReady$ itself triggers this request).
    const isMeRequest = req.url.includes('/users/me');

    if (isApiRequest && !isAuthRequest && !isMeRequest) {
      // Wait for authReady$ so the token is fresh before the first real request goes out
      return from(this.authService.authReady$).pipe(
        switchMap(() => {
          const token = this.storageService.getRaw('session', STORAGE_KEYS.session.token);
          const authedReq = this.addTokenAndCredentials(req, token);
          return next.handle(authedReq).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status === 401) {
                return this.handle401Error(authedReq, next, error);
              }
              return throwError(() => error);
            })
          );
        })
      );
    }

    // Auth requests, /me requests, and non-API requests go through immediately
    const token = this.storageService.getRaw('session', STORAGE_KEYS.session.token);
    if (isApiRequest) {
      req = this.addTokenAndCredentials(req, token);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Don't handle 401 for auth endpoints or the init /me call
        if (error.status === 401 && !isAuthRequest && !isMeRequest) {
          return this.handle401Error(req, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Add credentials and optionally token to request
   * withCredentials: true ensures cookies (httpOnly auth_token) are sent with cross-origin requests
   */
  private addTokenAndCredentials(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
    const headers: { [key: string]: string } = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return request.clone({
      setHeaders: headers,
      withCredentials: true
    });
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      },
      withCredentials: true
    });
  }

  /**
   * Show login modal when user gets a 401
   */
  private async showLoginModal(): Promise<boolean> {
    if (this.loginModalOpen) {
      return false;
    }

    this.loginModalOpen = true;

    try {
      const currentLayer = this.swNavigator.getActualLayer();
      console.log("currentLayer", currentLayer);
      
      const modal = await this.modalCtrl.create({
        component: LoginModalComponent,
        layer: currentLayer,
        modalId: 'login',
        backdropDismiss: false,
        cssClass: ['login-modal', 'remateriar-modal', 'modal-wrapper', 'remateriar-modal-height', 'modal-primary-background',],
      });

      await modal.present();

      const { data, role } = await modal.onWillDismiss();
      this.loginModalOpen = false;

      return role === 'success' && data?.success === true;
    } catch (error) {
      console.error('Error showing login modal:', error);
      this.loginModalOpen = false;
      return false;
    }
  }

  /**
   * Handle 401 errors — show login modal directly (no refresh token logic).
   * If user logs in successfully via modal, retry the original request.
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler, originalError: HttpErrorResponse): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>(observer => {
      from(this.showLoginModal()).subscribe(loginSuccess => {
        if (loginSuccess) {
          // User logged in successfully, retry the original request with new token
          const newToken = this.storageService.getRaw('session', STORAGE_KEYS.session.token);
          if (newToken) {
            const retryRequest = this.addToken(request, newToken);
            next.handle(retryRequest).subscribe({
              next: (event) => observer.next(event),
              error: (err) => observer.error(err),
              complete: () => observer.complete()
            });
          } else {
            observer.error(originalError);
          }
        } else {
          // User cancelled login — pass through the error
          observer.error(originalError);
        }
      });
    });
  }
}

